import { useAppDispatch, useAppSelector } from "../../../../store/types";
import Box from "@mui/material/Box";
import MediafileAutoComplete from "./MediafileAutoComplete";
import Skeleton from "@mui/material/Skeleton";
import { getTemporaryMsgMediaFile } from "../../../../services/temporaryMsg";
import { sortingCaseInSensitive } from "../utils";
import { useEffect, useState } from "react";
import styles from "../styles";
import useCss from "../../../../hooks/useCss";
import { updateLanguageId } from "../../../../slices/mediaMgtSlice";
import {
  MediaItem,
  MediaMgtDataContainer,
} from "../../../../slices/mediaMgtSlice";
import { getMediaMgtDataSelector } from "../../../../selectors/mediaMgtSelector";
import {
  getTemporaryMsgMediaFileInfoSelector,
  getTemporaryMsgMediaFileStatusSelector,
} from "../../../../selectors/temporaryMsgSelector";
import SbuxTranslate from "../../../../components/SbuxTranslate";
import type { MediaFileOption, MediaFileInfo } from "./types";

interface Props {
  languageId: string | null;
  handleLanguageMediaFile: (
    value: string,
    isAudioFileChanged?: boolean
  ) => void;
  row?: Record<string, unknown> | null;
  disabled?: boolean;
  maxWidth?: string | number;
  status?: string;
}

const TemporaryAudioConfig = ({
  languageId,
  handleLanguageMediaFile,
  row = null,
  disabled = false,
  maxWidth,
  status,
}: Props) => {
  const classes = useCss(styles);
  const dispatch = useAppDispatch();

  const mediaMgtData = useAppSelector(getMediaMgtDataSelector);
  const temporaryMsgMediaFileInfo = useAppSelector(
    getTemporaryMsgMediaFileInfoSelector
  ) as MediaFileInfo | null;
  const temporaryMsgMediaFileStatus = useAppSelector(
    getTemporaryMsgMediaFileStatusSelector
  );

  const [mediaFilesOnLanguageId, setMediaFilesOnLanguageId] = useState<
    MediaFileOption[]
  >([]);
  const [mediaFileInfo, setMediaFileInfo] = useState<MediaFileInfo | null>(
    null
  );
  const [selectedMediaFile, setSelectedMediaFile] = useState<string | null>(
    null
  );
  const [audioFileName, setAudioFileName] = useState("");
  const [hasError, setHasError] = useState(false);

  const getMediaItems = (): MediaItem[] => {
    if (!mediaMgtData) return [];
    if (Array.isArray(mediaMgtData)) return mediaMgtData as MediaItem[];
    return (mediaMgtData as MediaMgtDataContainer).data ?? [];
  };

  useEffect(() => {
    const mediaData = getMediaItems();
    let filteredData: MediaItem[] = [];
    if (languageId === null) {
      filteredData = mediaData.filter((item) => item.languageCode === "en");
    } else {
      filteredData = languageId
        ? mediaData.filter((item) => item.languageCode === languageId)
        : [];
    }
    dispatch(updateLanguageId(languageId ?? ""));
    const result: MediaFileOption[] = filteredData.reduce<MediaFileOption[]>(
      (acc, cur) => {
        acc.push({
          value: cur.filePath as string,
          name: cur.fileName as string,
        });
        return acc;
      },
      []
    );

    if (row) {
      const languages =
        Object.keys(row).length > 0 &&
        Object.keys(row).filter((a) => a.includes("message"));
      const mediaFileObj = languages
        ? (row[languages.find((a) => a.split("_")[1] === languageId) ?? ""] as
            | { value?: string }
            | undefined)
        : undefined;
      setSelectedMediaFile(
        mediaFileObj && mediaFileObj.value ? mediaFileObj.value : ""
      );
      if (mediaFileObj) handleMediaFileChange(mediaFileObj.value ?? "");
    }
    setMediaFilesOnLanguageId(result);
  }, [languageId]);

  useEffect(() => {
    if (temporaryMsgMediaFileInfo?.mediaContent) {
      setMediaFileInfo(temporaryMsgMediaFileInfo);
    }
  }, [temporaryMsgMediaFileInfo]);

  const playAudioFile = (file: string) => {
    return (
      <Box>
        {temporaryMsgMediaFileInfo === null &&
        temporaryMsgMediaFileStatus !== "success" ? (
          <Box sx={{ width: "18vw", height: 26, maxWidth }}>
            <Skeleton animation="wave" height={26} />
          </Box>
        ) : (
          <audio
            key={Math.random()}
            style={{
              height: 26,
              maxWidth: maxWidth as number | undefined,
            }}
            controls
            title={audioFileName}
          >
            {selectedMediaFile &&
            temporaryMsgMediaFileInfo?.languageCode === languageId ? (
              <source src={file} type="audio/wav" />
            ) : (
              <source src={undefined} type="audio/wav" />
            )}
          </audio>
        )}
      </Box>
    );
  };

  const handleMediaFileChange = async (
    value: string,
    isAudioFileChanged = false
  ) => {
    const mediaItems = getMediaItems();
    const filerData = mediaItems.find((item) => item.filePath === value);

    const data = {
      instanceId: (filerData?.instanceId as string) ?? "",
      languageCode: (filerData?.languageCode as string) ?? "",
      language: (filerData?.language as string) ?? "",
      fileName: encodeURIComponent((filerData?.filePath as string) ?? ""),
      mediaId: (filerData?.mediaId as string) ?? "",
      type: (filerData?.type as string) ?? "",
    };
    handleLanguageMediaFile(value, isAudioFileChanged);
    setSelectedMediaFile(value);
    setAudioFileName((filerData?.fileName as string) ?? "");

    if (value) {
      await dispatch(getTemporaryMsgMediaFile(data));
    }
  };

  const handleAudioFileValueChange = (value: string | null) => {
    handleMediaFileChange(value, true);
  };

  const handleErrorChange = (errorState: boolean) => {
    setHasError(errorState);
  };

  return (
    <Box className={classes.audioFileContainer}>
      <Box>
        <MediafileAutoComplete
          label={<SbuxTranslate>{"Audio File"}</SbuxTranslate>}
          data={sortingCaseInSensitive(mediaFilesOnLanguageId)}
          handleSelectChange={handleAudioFileValueChange}
          defaultValue={selectedMediaFile ?? ""}
          value={selectedMediaFile}
          disabled={disabled}
          required={true}
          onErrorChange={handleErrorChange}
          status={status}
        />
      </Box>

      <Box className={`${hasError ? classes.playerError : classes.player}`}>
        {mediaFileInfo?.mediaContent &&
          playAudioFile(mediaFileInfo.mediaContent)}
      </Box>
    </Box>
  );
};

export default TemporaryAudioConfig;
